import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, FileText, LogOut, Smartphone, Server, Menu, X, ChevronRight, Receipt, Activity, Package } from 'lucide-react';
import api, { clearAuthToken } from '../api';

export default function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        try { await api.post('/logout'); } catch (_) {}
        clearAuthToken();
        navigate('/login');
    };

    const navItems = [
        { path: '/', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/payments', label: 'Payment Methods', icon: CreditCard },
        { path: '/plans', label: 'Plans', icon: Package },
        { path: '/users', label: 'User Management', icon: Users },
        { path: '/servers', label: 'Servers', icon: Server },
        { path: '/transactions', label: 'Transactions', icon: Receipt },
        { path: '/announcements', label: 'Announcements', icon: Smartphone },
        { path: '/logs', label: 'Activity Log', icon: Activity },
    ];

    const currentPage = navItems.find(item => item.path === location.pathname);

    return (
        <div className="flex h-screen bg-[#0B0F1A] text-white font-sans overflow-hidden">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden animate-fade-in" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[260px] bg-[#0E1225]/95 backdrop-blur-2xl border-r border-white/[0.06] flex flex-col transition-transform duration-300 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
                <div className="p-6 pb-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-lg font-black gradient-text tracking-tight">
                            Admin Portal
                        </h1>
                        <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 text-slate-400 hover:text-white">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="mt-1 h-px bg-gradient-to-r from-blue-500/40 via-purple-500/20 to-transparent" />
                </div>

                <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`group flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all duration-200 relative ${isActive
                                    ? 'bg-blue-600/15 text-blue-400'
                                    : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200'
                                }`}
                            >
                                {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-blue-500 rounded-r-full" />
                                )}
                                <Icon size={18} className={`transition-colors ${isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
                                <span className="font-medium text-sm">{item.label}</span>
                                {isActive && <ChevronRight size={14} className="ml-auto text-blue-500/50" />}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-3 mt-auto">
                    <div className="h-px bg-white/[0.06] mb-3" />
                    <button
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-2.5 w-full text-slate-500 hover:text-red-400 hover:bg-red-500/[0.06] rounded-xl transition-all duration-200 group"
                    >
                        <LogOut size={18} className="group-hover:text-red-400 transition-colors" />
                        <span className="font-medium text-sm">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-auto bg-[#0B0F1A]">
                {/* Top bar for mobile */}
                <div className="sticky top-0 z-30 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/[0.04] px-4 py-3 lg:px-8 lg:py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.06] transition">
                            <Menu size={20} />
                        </button>
                        <div>
                            <h2 className="text-lg font-bold text-white">{currentPage?.label || 'Dashboard'}</h2>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                        <span className="text-xs text-slate-500 font-medium hidden sm:inline">System Online</span>
                    </div>
                </div>

                <div className="p-4 lg:p-8 max-w-7xl mx-auto animate-fade-in">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
